import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

const TTSContainer = styled.div`
  background: var(--color-background-paper);
  border: 1px solid var(--color-border-light);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-lg);
  margin: var(--spacing-lg) 0;
  box-shadow: var(--shadow-sm);
`;

const TTSHeader = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  margin-bottom: var(--spacing-md);
`;

const TTSIcon = styled.div`
  font-size: 1.5rem;
`;

const TTSTitle = styled.h3`
  font-family: var(--font-sans);
  font-size: var(--text-lg);
  font-weight: var(--font-semibold);
  color: var(--color-text-primary);
  margin: 0;
`;

const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-md);
`;

const ControlButton = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-sm) var(--spacing-md);
  border: none;
  border-radius: var(--border-radius-md);
  font-family: var(--font-sans);
  font-weight: var(--font-medium);
  cursor: pointer;
  transition: all var(--transition-normal);
  
  ${props => props.$variant === 'primary' ? `
    background: var(--color-primary);
    color: var(--color-text-inverse);
    
    &:hover:not(:disabled) {
      background: var(--color-primary-dark);
      transform: translateY(-1px);
      box-shadow: var(--shadow-md);
    }
  ` : `
    background: var(--color-gray-100);
    color: var(--color-text-primary);
    
    &:hover:not(:disabled) {
      background: var(--color-gray-200);
    }
  `}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const ProgressContainer = styled.div`
  margin: var(--spacing-md) 0;
`;

const ProgressLabel = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-xs);
  font-family: var(--font-sans);
  font-size: var(--text-sm);
  color: var(--color-text-secondary);
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: var(--color-gray-200);
  border-radius: var(--border-radius-full);
  overflow: hidden;
  cursor: pointer;
`;

const ProgressFill = styled.div<{ $progress: number }>`
  height: 100%;
  background: linear-gradient(90deg, var(--color-primary) 0%, var(--color-primary-dark) 100%);
  width: ${props => props.$progress}%;
  transition: width var(--transition-normal);
  border-radius: var(--border-radius-full);
`;

const CurrentTextDisplay = styled.div`
  background: var(--color-background-light);
  border: 1px solid var(--color-primary);
  border-radius: var(--border-radius-md);
  padding: var(--spacing-md);
  margin-top: var(--spacing-md);
  font-family: var(--font-serif);
  font-size: var(--text-sm);
  color: var(--color-text-primary);
  line-height: var(--leading-normal);
  max-height: 6rem;
  overflow: hidden;
  position: relative;
  box-shadow: var(--shadow-sm);
`;

const CurrentSentence = styled.div`
  background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%);
  padding: var(--spacing-sm);
  border-radius: var(--border-radius-sm);
  font-weight: var(--font-semibold);
  color: var(--color-text-primary);
  margin-bottom: var(--spacing-sm);
  border-left: 4px solid var(--color-primary-dark);
`;

const ReadingProgress = styled.div`
  font-size: var(--text-xs);
  color: var(--color-text-muted);
  margin-bottom: var(--spacing-xs);
`;

interface TextToSpeechControlsProps {
  storyId: string;
  title: string;
  content: string;
  isPlaying: boolean;
  isPaused: boolean;
  isLoading: boolean;
  progress: number;
  currentText: string;
  currentSentence: string;
  currentPosition: number;
  totalLength: number;
  onPlay: () => void;
  onPause: () => void;
  onResume: () => void;
  onStop: () => void;
  onSetPosition: (position: number) => void;
}

const TextToSpeechControls: React.FC<TextToSpeechControlsProps> = ({
  title,
  content,
  isPlaying,
  isPaused,
  isLoading,
  progress,
  currentText,
  currentSentence,
  currentPosition,
  totalLength,
  onPlay,
  onPause,
  onResume,
  onStop,
  onSetPosition,
}) => {
  const { t } = useTranslation();
  const fullText = `${title}. ${content}`;
  
  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const newProgress = (clickX / rect.width) * 100;
    const newPosition = Math.floor((newProgress / 100) * fullText.length);
    onSetPosition(newPosition);
  };

  const formatProgress = (prog: number) => `${Math.round(prog)}%`;

  if (!('speechSynthesis' in window)) {
    return (
      <TTSContainer>
        <TTSHeader>
          <TTSIcon>🔊</TTSIcon>
          <TTSTitle>{t('tts.listenToStory')}</TTSTitle>
        </TTSHeader>
        <p style={{ color: 'var(--color-text-secondary)', fontFamily: 'var(--font-sans)' }}>
          {t('tts.notSupported')}
        </p>
      </TTSContainer>
    );
  }

  return (
    <TTSContainer>
      <TTSHeader>
        <TTSIcon>🔊</TTSIcon>
        <TTSTitle>{t('tts.listenToStory')}</TTSTitle>
      </TTSHeader>

      <ControlsRow>
        {!isPlaying && !isPaused && (
          <ControlButton $variant="primary" onClick={onPlay} disabled={isLoading}>
            {isLoading ? '⏳' : '▶️'} {progress >= 100 ? t('tts.replay') : t('tts.play')}
          </ControlButton>
        )}
        
        {isPlaying && !isPaused && (
          <ControlButton onClick={onPause}>
            ⏸️ {t('tts.pause')}
          </ControlButton>
        )}
        
        {isPaused && (
          <ControlButton $variant="primary" onClick={onResume}>
            ▶️ {t('tts.resume')}
          </ControlButton>
        )}
        
        {(isPlaying || isPaused) && (
          <ControlButton onClick={onStop}>
            ⏹️ {t('tts.stop')}
          </ControlButton>
        )}
      </ControlsRow>

      <ProgressContainer>
        <ProgressLabel>
          <span>{t('tts.progress')}</span>
          <span>{formatProgress(progress)}</span>
        </ProgressLabel>
        <ProgressBar onClick={handleProgressClick}>
          <ProgressFill $progress={progress} />
        </ProgressBar>
      </ProgressContainer>

      {(isPlaying || isPaused) && (
        <CurrentTextDisplay>
          <ReadingProgress>
            {t('tts.readingPosition')}: {currentPosition.toLocaleString()} / {totalLength.toLocaleString()} characters
          </ReadingProgress>
          
          {currentSentence && (
            <CurrentSentence>
              <strong>🎯 {t('tts.currentlyReading')}:</strong> {currentSentence}
            </CurrentSentence>
          )}
          
          <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
            <strong>{t('tts.next')}:</strong> {currentText.substring(0, 100)}
            {currentText.length > 100 && '...'}
          </div>
        </CurrentTextDisplay>
      )}
    </TTSContainer>
  );
};

export default TextToSpeechControls;